import { cn } from '@/lib/utils';
import React, { useEffect } from 'react';
import { type DimensionValue, type StyleProp, type ViewStyle } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

interface SkeletonProps {
  width?: DimensionValue;
  height?: DimensionValue;
  /** Corner radius in px. Defaults to the system 8px radius. */
  radius?: number;
  className?: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

const PULSE_MIN = 0.4;
const PULSE_MAX = 0.8;
const PULSE_DURATION = 900;

/**
 * A tonal loading placeholder. Renders a `surface-secondary` block that gently
 * pulses its opacity while content loads, holding the layout's height so the
 * page does not reflow when data arrives. Honors Reduce Motion by rendering a
 * static block. Decorative — hidden from assistive technology.
 */
export function Skeleton({
  width,
  height,
  radius = 8,
  className,
  style,
  testID,
}: SkeletonProps) {
  const reducedMotion = useReducedMotion();
  const opacity = useSharedValue(reducedMotion ? PULSE_MAX : PULSE_MIN);

  useEffect(() => {
    if (reducedMotion) {
      opacity.set(PULSE_MAX);
      return;
    }
    opacity.set(withRepeat(
      withTiming(PULSE_MAX, {
        duration: PULSE_DURATION,
        easing: Easing.inOut(Easing.ease),
      }),
      -1,
      true
    ));
  }, [reducedMotion, opacity]);

  const animatedStyle = useAnimatedStyle(() => ({ opacity: opacity.get() }));

  return (
    <Animated.View
      testID={testID}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      className={cn('bg-surface-secondary', className)}
      style={[{ width, height, borderRadius: radius }, style, animatedStyle]}
    />
  );
}
